import React from 'react';
import { Users, TrendingUp, Settings, Heart } from 'lucide-react';
import { motion } from 'framer-motion';

const attendees = [
  {
    icon: Users,
    title: 'Salon & Spa Owners',
    description: 'Business owners looking to scale operations and increase profitability through automation',
    benefits: ['Reduce operational overhead', 'Increase revenue streams', 'Improve customer satisfaction']
  },
  {
    icon: TrendingUp,
    title: 'Franchise Managers',
    description: 'Multi-location managers seeking standardized processes and centralized control',
    benefits: ['Standardize operations', 'Monitor multiple locations', 'Streamline reporting']
  },
  {
    icon: Settings,
    title: 'Operations Managers',
    description: 'Managers responsible for daily operations and staff coordination',
    benefits: ['Automate scheduling', 'Optimize staff productivity', 'Reduce manual tasks']
  },
  {
    icon: Heart,
    title: 'Customer Experience Leaders',
    description: 'Professionals focused on improving client satisfaction and retention',
    benefits: ['Enhance client journey', 'Increase loyalty', 'Personalize experiences']
  }
];

const WhoShouldAttend: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Who Should Attend This Demo?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            This personalized demo is perfect for beauty industry professionals ready to transform their business operations
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {attendees.map((attendee, index) => {
            const IconComponent = attendee.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-3 rounded-lg flex-shrink-0">
                    <IconComponent className="h-8 w-8 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-black mb-3">{attendee.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{attendee.description}</p>
                    <ul className="space-y-2">
                      {attendee.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="flex items-center text-gray-700">
                          <div className="w-2 h-2 bg-red-600 rounded-full mr-3"></div>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <div className="bg-red-50 rounded-2xl p-8 inline-block">
            <h3 className="text-2xl font-bold text-black mb-4">
              Perfect for Teams of Any Size
            </h3>
            <p className="text-gray-700 text-lg">
              Whether you run a single salon or manage multiple locations, 
              this demo will show you exactly how Zylu scales with your business.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhoShouldAttend;